import { GET_BASKET, GET_BASKET_ERROR, GET_BASKET_SUCCESS } from "core-web/state/models/Basket/constants";

import Events from "core-web/libs/Events";
import { ErrorEvents } from "core-web/libs/Events/constants";
import GetBasket from "core-web/libs/GrebbCommerceAPI/Basket/GetBasket";
import { acquireMutex } from "core-web/state";
import createBasket from "core-web/state/models/Basket/dispatches/createBasket";
import removeBasket from "core-web/state/models/Basket/dispatches/removeBasket";
import clearBasketIdCookie from "core-web/state/models/Basket/utils/clearBasketIdCookie";
import getBasketId from "core-web/state/models/Basket/utils/getBasketId";
import setBasketItemsAmountCookie from "core-web/state/models/Basket/utils/setBasketItemsAmountCookie";
import setUniqueBasketItemsAmountCookie from "core-web/state/models/Basket/utils/setUniqueBasketItemsAmountCookie";
import store from "core-web/state/store";
import buildCartProductTree from "core-web/utils/buildCartProductTree";
import { BASKET_PROMOTION_ITEM_DATA } from "theme/config/constants";
import getBasketInfoFieldByCode from "../utils/getBasketInfoFieldByCode";
import validateMemberPromotions from "./validateMemberPromotions";

const handleFailedBasket = async () => {
    clearBasketIdCookie();
    const createBasketResponse = await createBasket();
    const basketId = createBasketResponse.data.id;
    return await GetBasket(basketId);
};

export const getBasket = () => async (dispatch) => {
    const basketId = await getBasketId();
    const mutexLock = await acquireMutex("basket.getBasket");
    let response;
    let basket;
    dispatch({ type: GET_BASKET });

    try {
        response = await GetBasket(basketId);
        if (response?.data) {
            if (response.data.is_editable === false) {
                mutexLock();
                Events.trigger(ErrorEvents.LOG, { type: "Storm uneditable basket", message: basketId });
                await removeBasket();
                return;
            }

            const basketItemsTree = buildCartProductTree(response.data.items);
            const basketItemsCount = basketItemsTree.reduce((total, item) => total + (item.quantity || 0), 0);
            setBasketItemsAmountCookie(basketItemsCount);
            setUniqueBasketItemsAmountCookie(basketItemsTree.length);

            basket = {
                basketId,
                items: basketItemsTree,
                currency: response.data.currency_id || null,
                summary: response.data.format.summary || [],
                comment: response.data.comment,
                textComments: response.data.format.text_comment || [],
                promotions: response.data.applied_promotions,
                info: response.data.info || [],
            };

            const basketPromotionItemData = JSON.parse(
                getBasketInfoFieldByCode(BASKET_PROMOTION_ITEM_DATA, basket.info)?.value || "{}",
            );
            const activeMemberPromotions = Object.keys(basketPromotionItemData).reduce((promotions, partNo) => {
                const currentProductPromotions = basketPromotionItemData?.[partNo];

                if (!currentProductPromotions) {
                    return promotions;
                }

                currentProductPromotions.forEach((appliedPromotion) => {
                    const { id, discount_amount: discountAmount } = appliedPromotion;

                    if (id === null) {
                        return promotions;
                    }

                    if (promotions.some((promotion) => promotion.id === id)) {
                        promotions = promotions.map((promotion) => {
                            if (promotion.id === id) {
                                return Object.assign(promotion, {
                                    discountAmount: promotion.discountAmount + discountAmount,
                                });
                            }

                            return promotion;
                        });
                    } else {
                        promotions.push({ id, discountAmount });
                    }
                });

                return promotions;
            }, []);

            const memberPromotionDiscountAmount = activeMemberPromotions.reduce(
                (discount, { discountAmount: promotionDiscountAmount }) => (discount += promotionDiscountAmount || 0),
                0,
            );

            dispatch({
                type: GET_BASKET_SUCCESS,
                basketId,
                items: basket.items,
                currency: basket.currency,
                summary: basket.summary,
                comment: basket.comment,
                textComments: basket.textComments,
                promotions: basket.promotions,
                info: basket.info,
                activeMemberPromotions,
                memberPromotionDiscountAmount,
            });

            mutexLock();

            if (activeMemberPromotions.length) {
                validateMemberPromotions(activeMemberPromotions.map(({ id }) => id));
            }
        } else {
            mutexLock();
            // This basket was not found.
            // Create a new one.
            dispatch({ type: GET_BASKET_ERROR });
            response = await handleFailedBasket();
        }
    } catch (e) {
        console.error(e);
        dispatch({ type: GET_BASKET_ERROR });
        // This basket was not found.
        // Create a new one.
        response = await handleFailedBasket();

        mutexLock();
    }

    return basket || response;
};

export default () => store.dispatch(getBasket());
